import { useEffect, useState } from 'react';
import { FileUpload } from '../DataMaintenance/FileUpload';
import { t } from 'i18next';
import { Box, Grid, Button, Badge } from '@mui/material';
import {
  ECellType,
  ECommonDialogType,
  TableHeaders,
  foreignKeysForTable,
  initialSortField,
  EDataMaintenanceType,
  EDataTableType,
  EModuleType,
  EActionType,
  RecordLength,
  deleteFields,
  ECompareStatusCellValues,
  EApplicability,
  MaxDeleteCount,
  EStatus,
} from '../../data/Constants';
import {
  DataGrid,
  GridColDef,
  GridCellParams,
  FilterPanelTrigger,
  Toolbar,
  ColumnsPanelTrigger,
  GridRowSelectionModel,
  ToolbarButton,
  GridViewColumnIcon,
  GridFilterListIcon,
  useGridApiContext,
  useGridSelector,
  gridFilterModelSelector,
} from '@mui/x-data-grid';
import { ExportDetails } from '../ExportDetails';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CompareIcon from '@mui/icons-material/Compare';
import { AppDispatch, AppState } from '../../store';

import { useDispatch, useSelector } from 'react-redux';
import { setDialogMessage } from '../../store/states/DialogSlice';
import { setAlertMessage } from '../../store/states/AlertMessageSlice';
import {
  TableSearchBar,
  TableRecordPicker,
  TablePaginationRow,
  renderNoRecordsMessage,
  renderCell,
  createBaseColumn,
  sharedDataGridProps,
} from './Helper/DataTableHelper';

import type {
  IMaintenanceTableProps,
  ITableHeaders,
  IDataTableRow,
  IStateData,
  IOpenDialogFn,
  IHandleSearchProps,
  IHandleRecordsChangeProps,
  IHandleRefreshProps,
  IDisableDeleteBtnProps,
  ICustomToolbarProps,
  IButtonSectionProps,
  IRenderButtonSectionProps,
  IDeleteFieldConfig,
} from '../../../types';

const displayImport = {
  [EDataMaintenanceType.SalesText]: true,
  [EDataMaintenanceType.Images]: false,
  [EDataMaintenanceType.ProductHierarchy]: true,
  [EDataMaintenanceType.Prices]: true,
  [EDataMaintenanceType.LanguageAssociation]: false,
  [EDataMaintenanceType.MarketAssociation]: false,
};

//This method return the class name for the dataTable columns
const getHeaderClass = (type: string, header: ITableHeaders) => {
  if (
    (type === EDataTableType.PropertyAssociation ||
      type === EDataTableType.FeaturePropertyAssociation) &&
    header?.applicability?.includes(EApplicability.Master)
  ) {
    return 'property-header';
  }
  return '';
};

const createGridColumns = (
  tableHeaders: ITableHeaders[],
  openDialog: IOpenDialogFn,
  uniqueKey: string,
  type: string,
  props: IMaintenanceTableProps
) => {
  return tableHeaders.map((header) => {
    const base = createBaseColumn(header, (params) =>
      renderCell(params, header, {
        openDialog,
        uniqueKey,
        type,
        moduleType: props.moduleType,
        props,
      })
    );
    return {
      ...base,
      headerClassName: getHeaderClass(type, header),
      cellClassName: (params: GridCellParams) => {
        const baseCls = (base.cellClassName as (p: GridCellParams) => string)(params);
        if (baseCls) return baseCls;
        if (params.row[ECompareStatusCellValues.Status] === ECompareStatusCellValues.Added) {
          return 'cellStylesPH' + ECompareStatusCellValues.Added;
        }
        if (params.row[ECompareStatusCellValues.Status] === ECompareStatusCellValues.Deleted) {
          return 'cellStylesPH' + ECompareStatusCellValues.Deleted;
        }
        if (params.row[ECompareStatusCellValues.Status] === ECompareStatusCellValues.Updated) {
          return params.row[ECompareStatusCellValues.ModifiedCells]
            ?.map((modValue: string) => modValue.toLowerCase())
            .includes(header.field.toLowerCase())
            ? 'cellStylesPH' + ECompareStatusCellValues.Updated
            : '';
        }
        return '';
      },
    };
  });
};

const handleSearch = (props: IHandleSearchProps) => {
  const { newVal, recordsPerPage, token, getDataList, setPage, productId, dispatch, type } = props;
  dispatch(
    getDataList({
      token,
      page: 1,
      limit: recordsPerPage,
      searchCode: newVal,
      productId: productId,
      reportType: type,
    })
  );
  setPage(1);
};

const handleRecordsChange = (handleRecordsChangeProps: IHandleRecordsChangeProps) => {
  const { records, searchValue, setPage, productId, props, dispatch } = handleRecordsChangeProps;
  dispatch(
    props.getDataList({
      token: props.token,
      page: 1,
      limit: records,
      searchCode: searchValue,
      productId: productId,
      reportType: props.type,
    })
  );
  setPage(1);
};

const handleRefresh = (handleRefreshProps: IHandleRefreshProps) => {
  const { props, page, recordsPerPage, productId, dispatch } = handleRefreshProps;
  dispatch(
    props.getDataList({
      token: props.token,
      page: page,
      limit: recordsPerPage,
      searchCode: null as unknown as string,
      productId: productId,
      reportType: props.type,
    })
  );
};

function loadFormData(
  props: IMaintenanceTableProps,
  type: string,
  tableData: IStateData,
  page: number,
  recordsPerPage: number,
  searchValue: string,
  dispatch: AppDispatch
) {
  //To make an API call to store data in global state if not available or  Make an API call when it's Compare Product Hierarchy
  if (
    type === EDataMaintenanceType.CompareProductHierarchy ||
    type === EDataMaintenanceType.FeaturePropertyAssociation ||
    type === EDataTableType.LovOptions ||
    !tableData.data[1]
  ) {
    dispatch(
      props.getDataList({
        token: props.token,
        page: page,
        limit: recordsPerPage,
        searchValue: searchValue,
        productId: props.productId,
        reportType: props.type,
      })
    );
  }
}

function inverseCheckForArray(
  deleteField: string[],
  fdata: IDataTableRow,
  inverseCheckForDelete: boolean[]
) {
  let inverseCheck = false;
  for (let i = 0; i < deleteField.length; i++) {
    inverseCheck =
      inverseCheck ===
      (inverseCheckForDelete[i] ? !fdata[deleteField[i]] : Boolean(fdata[deleteField[i]]));
  }
  return inverseCheck;
}

function disableDeleteBtn(disableDeleteProps: IDisableDeleteBtnProps) {
  const {
    filteredData,
    selectionModel,
    deleteField,
    inverseCheckForDelete,
    setDisableDelete,
    totalCount,
    type,
  } = disableDeleteProps;
  const nonDeleteEntries = filteredData.filter((fdata: IDataTableRow) => {
    //Enable delete button based on isDefault and isActive fields
    const innerEntries = Array.from(selectionModel.ids).filter((entry) => {
      let inverseCheck = false;
      if (Array.isArray(deleteField)) {
        inverseCheck = inverseCheckForArray(deleteField, fdata, inverseCheckForDelete as boolean[]);
      } else {
        inverseCheck = (inverseCheckForDelete as boolean)
          ? !fdata[deleteField]
          : Boolean(fdata[deleteField]);
      }
      return inverseCheck && entry === fdata['id']; //using id instead of uniqueKey as we are now assigning unique id to each and every row of data
    }).length;
    return innerEntries !== 0;
  }).length;
  if (
    (selectionModel.ids.size > 0 && nonDeleteEntries !== selectionModel.ids.size) ||
    (type === EDataTableType.Prices && totalCount > 1)
  ) {
    setDisableDelete(false);
  } else {
    setDisableDelete(true);
  }
}

function displayCheckboxSelection(moduleType: string, type: string) {
  return (
    moduleType === EModuleType.Administration ||
    moduleType === EModuleType.Notifications ||
    moduleType === EModuleType.FeatureFlags ||
    (moduleType === EModuleType.Datamaintainance && type === EDataTableType.Languages) ||
    type === EDataTableType.Ace ||
    type === EDataTableType.Queue ||
    type === EDataTableType.Markets ||
    type === EDataTableType.Properties ||
    type === EDataTableType.MultiNumericFeature ||
    type === EDataTableType.Lov ||
    type === EDataTableType.LovOptions
  );
}

const compareButton = (handleCompare: () => void) => {
  return (
    <>
      &emsp;
      <Button
        id="publish-button"
        variant="contained"
        onClick={handleCompare}
        startIcon={<CompareIcon id="compare-icon" />}
        className="common-btn text-capitalize"
      >
        {t('button.compare')}
      </Button>
    </>
  );
};

const ButtonSection = ({
  disableDelete,
  handleCreate,
  handleDelete,
  handleCompare,
  type,
}: IButtonSectionProps): React.JSX.Element | null => {
  if (type === EDataMaintenanceType.ProductHierarchy) {
    return compareButton(handleCompare); // Assuming compareButton is a valid component
  } else if (
    type !== EDataMaintenanceType.Queue &&
    type !== EDataMaintenanceType.ProductRelease &&
    type !== EDataMaintenanceType.ProductVersionMapping
  ) {
    return (
      <>
        &emsp;
        <Button
          data-testid="data-table-delete-button"
          className="delete-button common-btn"
          color="error"
          variant="contained"
          disabled={disableDelete}
          startIcon={<DeleteIcon />}
          onClick={handleDelete}
        >
          {type === EDataMaintenanceType.Prices ? t('button.deleteAll') : t('button.delete')}
        </Button>
        &emsp;
        {type !== EDataMaintenanceType.Prices &&
          type !== EDataMaintenanceType.Ace &&
          type !== EDataMaintenanceType.Queue &&
          type !== EDataMaintenanceType.Lov &&
          type !== EDataTableType.LovOptions && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  handleCreate();
                }}
                className="text-capitalize common-btn"
              >
                {t('button.new')}
              </Button>
              {type === EDataTableType.Users && (
                <>
                  &emsp;
                  <ExportDetails />
                </>
              )}
            </>
          )}
      </>
    );
  }
  return null;
};

/**
 * Render the buttons(New and Delete ) in the data table
 * @param renderButtonSectionProps input props
 * @returns {React.JSX.Element} react element
 */
const renderButtonSection = (
  renderButtonSectionProps: IRenderButtonSectionProps
): React.JSX.Element | null => {
  const {
    moduleType,
    type,
    disableDelete,
    handleCreate,
    handleDelete,
    handleCompare,
    generateReport,
  } = renderButtonSectionProps;

  if (moduleType === EModuleType.Reports) {
    return (
      <Button variant="contained" className="text-capitalize common-btn" onClick={generateReport}>
        {t('button.generate')}
      </Button>
    );
  }

  return type === EDataTableType.ProductHierarchy ||
    moduleType === EModuleType.Administration ||
    moduleType === EModuleType.FeatureFlags ||
    moduleType === EModuleType.Feature ||
    moduleType === EModuleType.Notifications ||
    (moduleType === EModuleType.Datamaintainance &&
      (type === EDataTableType.Languages ||
        type === EDataTableType.Prices ||
        type === EDataTableType.Lov ||
        type === EDataTableType.LovOptions)) ||
    type === EDataTableType.Markets ||
    type === EDataTableType.Properties ? (
    <ButtonSection
      type={type}
      disableDelete={disableDelete}
      handleCreate={handleCreate}
      handleDelete={handleDelete}
      handleCompare={handleCompare}
    />
  ) : null;
};

const renderImportButton = (
  type: string,
  token: string,
  displayImport: Record<string, boolean>
) => {
  return displayImport[type] ? <FileUpload token={token} type={type} /> : null;
};

function CustomToolbar({
  props,
  page,
  displayImport,
  moduleType,
  disableDelete,
  handleCreate,
  handleDelete,
  handleCompare,
  handleRecordsPerPageChange,
  searchValue,
  token,
  recordsPerPage,
  type,
  setSearchValue,
  setPage,
  getDataList,
  dispatch,
  productId,
  generateReport,
}: Readonly<ICustomToolbarProps>) {
  const apiRef = useGridApiContext();
  // Get active filter count reactively - only count filters with values
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const filterCount =
    filterModel?.items?.filter(
      (item) => item.value !== undefined && item.value !== null && item.value !== ''
    ).length || 0;

  return (
    <Toolbar>
      <Grid className="w-100" container spacing={2}>
        <Grid className="d-flex align-center" size={6}>
          <TableSearchBar
            value={searchValue}
            placeholder={TableHeaders[type].main
              .filter((col) => col.searchable)
              .map((col) => t('labels.' + col.field))
              .join(', ')}
            onChange={(newVal) => {
              setSearchValue(newVal);
              handleSearch({
                newVal,
                recordsPerPage,
                token,
                getDataList,
                setPage,
                productId,
                dispatch,
                type,
              });
            }}
          />
          &emsp;
          <TableRecordPicker value={recordsPerPage} onChange={handleRecordsPerPageChange} />
          &emsp;
          <ColumnsPanelTrigger
            render={
              <ToolbarButton
                render={<Button size="small" startIcon={<GridViewColumnIcon fontSize="small" />} />}
              >
                {t('labels.columns')}
              </ToolbarButton>
            }
          />
          &emsp;
          <FilterPanelTrigger>
            <Button
              size="small"
              startIcon={
                <Badge variant="dot" color="primary" invisible={filterCount === 0}>
                  <GridFilterListIcon />
                </Badge>
              }
            >
              {t('labels.filters')}
            </Button>
          </FilterPanelTrigger>
          &emsp;
          <Button
            className="refresh-button common-btn"
            color="primary"
            variant="text"
            startIcon={<RefreshIcon />}
            onClick={() => {
              handleRefresh({ props, page, recordsPerPage, productId, dispatch });
            }}
          >
            {t('button.refresh')}
          </Button>
        </Grid>
        <Grid className="d-flex justify-end" size={6}>
          {renderImportButton(type, token, displayImport)}
          {renderButtonSection({
            moduleType,
            type,
            token,
            disableDelete,
            handleCreate,
            handleDelete,
            handleCompare,
            generateReport,
          })}
        </Grid>
      </Grid>
    </Toolbar>
  );
}

const getTableClassName = (type: string) => {
  return `maintenanceTable ${type === EDataTableType.FeaturePropertyAssociation ? 'dialog-mode' : ''}`;
};

const getRowHighlighter = (type: string): string | undefined => {
  const columnDetails = TableHeaders[type].main.find((header) => header.rowHighlighter === true);
  return columnDetails?.field;
};

export const MaintenanceTable = (props: IMaintenanceTableProps) => {
  const { token, type, getDataList, moduleType } = props;
  const [dataGridKey, setDataGridKey] = useState(''); //Key is used for proper functionality of DataGrid(like Filter feature)
  const [searchValue, setSearchValue] = useState<string>('');
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RecordLength[0]); //Default records size to be fetched from RecordLength
  const [page, setPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);
  const [disableDelete, setDisableDelete] = useState<boolean>(false);
  const tableHeaders: ITableHeaders[] =
    type === EDataTableType.PropertyAssociation ||
    type === EDataTableType.FeaturePropertyAssociation
      ? (props.columns as ITableHeaders[])
      : TableHeaders[type].main;
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  });
  const deleteField = Array.isArray(
    (deleteFields as Record<string, IDeleteFieldConfig | IDeleteFieldConfig[]>)[type]
  )
    ? (deleteFields as unknown as Record<string, IDeleteFieldConfig[]>)[type].map(
        (val: IDeleteFieldConfig) => val.field
      )
    : (deleteFields as unknown as Record<string, IDeleteFieldConfig>)[type]?.field;
  const rowHighlighter = getRowHighlighter(type);
  const inverseCheckForDelete = Array.isArray(
    (deleteFields as Record<string, IDeleteFieldConfig | IDeleteFieldConfig[]>)[type]
  )
    ? (deleteFields as unknown as Record<string, IDeleteFieldConfig[]>)[type].map(
        (val: IDeleteFieldConfig) => val.inverseCheck
      )
    : (deleteFields as unknown as Record<string, IDeleteFieldConfig>)[type]?.inverseCheck;
  const uniqueKey = foreignKeysForTable[type];
  const sortingKey = initialSortField[type];
  const productId = props.productId;
  const tableData = useSelector((state: AppState) => {
    if (moduleType === 'notifications') {
      if (type === 'queue') {
        return state.queue;
      }
      if (type === 'ace') {
        return state.notifications;
      }
      if (type === 'productRelease') {
        return state.productRelease;
      }
      if (type === 'productVersionMapping') {
        return state.productVersionMapping;
      }
    }
    if (moduleType === EModuleType.Reports) {
      return state[moduleType];
    }
    return (state as unknown as Record<string, Record<string, IStateData>>)[moduleType]?.[type];
  }) as IStateData;
  const dispatch = useDispatch<AppDispatch>();
  const currentPageData: IDataTableRow[] = tableData?.data?.[page] ?? [];
  const openDialog = (
    data: IDataTableRow | Record<string, unknown>,
    dialogType: string,
    action?: string,
    header?: ITableHeaders
  ) => {
    switch (dialogType) {
      case ECellType.ObjectChips:
      case ECellType.ArrayChips:
        dispatch(
          setDialogMessage({
            show: true,
            message: data,
            type: dialogType,
            manipulationType: action,
            header: header,
            uniqueKey: uniqueKey,
          })
        );
        break;
      case ECellType.Details:
        dispatch(
          setDialogMessage({
            show: true,
            message: data,
            type: dialogType,
            manipulationType: action,
            header: header,
            detailsViewType: type,
            uniqueKey: uniqueKey,
          })
        );
        break;

      case EDataMaintenanceType.ProductHierarchy:
        dispatch(
          setDialogMessage({
            show: true,
            message: data,
            type: dialogType,
            manipulationType: action,
            moduleType: moduleType,
          })
        );
        break;
      case ECommonDialogType.Image:
      case ECommonDialogType.LongSalesText:

      case ECommonDialogType.ShortSalesText:

      case ECommonDialogType.Notification:

      case ECommonDialogType.DeletePrices:
        dispatch(
          setDialogMessage({
            show: true,
            message: data,
            type: dialogType,
            manipulationType: dialogType,
            moduleType: moduleType,
          })
        );
        break;
      case ECellType.Log:
        dispatch(
          setDialogMessage({
            show: true,
            title: `${t('labels.log')} (${(data as IDataTableRow).code || (data as IDataTableRow).id})`,
            message: props.getLogContent
              ? props.getLogContent((data as IDataTableRow).log as string, t)
              : '',
            type: dialogType,
            manipulationType: dialogType,
            moduleType: moduleType,
            notificationCode: (data as IDataTableRow).code,
            tableDataType: props.type,
          })
        );
        break;
      case ECellType.SyncStatus:
        dispatch(
          setDialogMessage({
            show: true,
            title: `${t('labels.sync')} (${(data as IDataTableRow).code})`,
            message: props.getLogContent((data as IDataTableRow).syncStatus as string, t),
            type: dialogType,
            manipulationType: dialogType,
            moduleType: moduleType,
            notificationCode: (data as IDataTableRow).code,
          })
        );
        break;

      case ECommonDialogType.RetriggerProductRelease:
        dispatch(
          setDialogMessage({
            show: true,
            title: `${t('labels.retrigger')}`,
            message: { id: data.id },
            type: dialogType,
            manipulationType: dialogType,
            moduleType: moduleType,
          })
        );
        break;
      case ECellType.Error:
        dispatch(
          setDialogMessage({
            show: true,
            title: `${t('labels.error')} (${(data as IDataTableRow).code})`,
            message: props.getLogContent((data as IDataTableRow).syncStatus as string, t),
            type: dialogType,
            manipulationType: dialogType,
            moduleType: moduleType,
            notificationCode: (data as IDataTableRow).code,
          })
        );
        break;
      default: {
        const req = structuredClone(Array.from(selectionModel.ids));
        const selectedIds = currentPageData
          .filter((dataSelected) => req.includes(dataSelected.id))
          .map((validData) => validData[uniqueKey]);

        const context = {
          ...data,
          searchValue,
          recordsPerPage,
          page,
          selectedIds,
        };
        const newdata = typeof data?.message === 'string' ? data.message : context;

        let title = null;
        if (type === EDataTableType.Ace) {
          title = t('header.notification');
        } else if (type === EDataTableType.Queue) {
          title = t('header.queue');
        }

        dispatch(
          setDialogMessage({
            show: true,
            title: title,
            message: data?.message ? newdata : context,
            type: dialogType,
            manipulationType: action,
            moduleType: moduleType,
            callDeleteAPI: callDeleteAPI,
          })
        );
      }
    }
  };

  const columns: GridColDef[] = createGridColumns(
    tableHeaders,
    openDialog,
    uniqueKey,
    type,
    props
  ) as GridColDef[];

  useEffect(() => {
    loadFormData(props, type, tableData, page, recordsPerPage, searchValue, dispatch);
  }, []);

  useEffect(() => {
    if (!tableData) return;
    setSearchValue(tableData.searchKey || '');
    setRecordsPerPage(tableData.recordsPerPage || RecordLength[0]);
    setPageCount(Math.ceil(tableData.totalCount / tableData.recordsPerPage) || 1);
  }, [tableData]);

  useEffect(() => {
    disableDeleteBtn({
      filteredData: currentPageData,
      selectionModel,
      deleteField,
      inverseCheckForDelete,
      setDisableDelete,
      totalCount: tableData?.totalCount ?? 0,
      type,
    });
    setDataGridKey(searchValue + recordsPerPage + page);
  });

  const handleRecordsPerPageChange = (records: number) => {
    setRecordsPerPage(records);
    handleRecordsChange({
      page,
      records,
      type,
      searchValue,
      setPage,
      productId,
      props,
      dispatch,
    });
  };

  const handlePaginationChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    if (!tableData?.data?.[value]) {
      dispatch(
        props.getDataList({
          token: props.token,
          page: value,
          limit: recordsPerPage,
          searchCode: searchValue,
          productId: productId,
          reportType: props.type,
        })
      );
    }
  };

  const handleCreate = () => {
    openDialog({}, type, EActionType.Create);
  };

  /**
   * This method triggers when click on the delete button in the data table to delete the selected records
   * @returns { void }
   */
  const handleDelete = (): void => {
    const maxCount = MaxDeleteCount[type] ?? MaxDeleteCount['Default'];

    //show the error message if selected records are more than 10.
    if (selectionModel.ids.size > maxCount) {
      dispatch(
        setAlertMessage({
          show: true,
          message: t('messages.deleteLimitMessage', { limit: maxCount }),
          type: 'error',
        })
      );
    } else {
      //show the confirm dialog  message to delete the records
      openDialog({}, type, EActionType.Delete);
    }
  };

  const handleCompare = () => {
    openDialog({}, type, EActionType.Compare);
  };

  const callDeleteAPI = () => {
    const selectedIdsArray = structuredClone(Array.from(selectionModel.ids));
    const selectedIds = currentPageData
      .filter((f) => selectedIdsArray.includes(f.id ? f.id : f.code) && (f.isActive || f.active))
      .map((f1) => f1[uniqueKey] as string);
    if (type === EDataTableType.ApiKeys) {
      const selectedKeys = selectedIds.map(
        (id) => currentPageData.find((fid) => (fid.id ? fid.id : fid.code) === id)?.key as string
      );
      props.deleteItems?.(selectedIds, selectedKeys);
    } else {
      props.deleteItems?.(selectedIds);
    }
    dispatch(setDialogMessage({ show: false }));
    setSelectionModel({ type: 'include', ids: new Set() });
  };

  function displayRowStatusFailedColor(fieldValue: string) {
    return rowHighlighter && fieldValue === EStatus.Failed ? 'statusFailed' : '';
  }

  const tableGridProps = {
    ...sharedDataGridProps,
    getRowId: (row: IDataTableRow) => row.id ?? row.code ?? '',
    initialState: {
      sorting: {
        sortModel: sortingKey ? [{ field: sortingKey, sort: 'asc' as const }] : [],
      },
    },
    slots: {
      toolbar: () =>
        CustomToolbar({
          props,
          page,
          displayImport,
          moduleType,
          disableDelete,
          handleCreate,
          handleDelete,
          handleCompare,
          handleRecordsPerPageChange,
          searchValue,
          token,
          recordsPerPage,
          type,
          setSearchValue,
          setPage,
          getDataList,
          dispatch,
          productId,
          generateReport: props.generateReport,
        }),
      noRowsOverlay: () => renderNoRecordsMessage(searchValue),
    },
    checkboxSelection: displayCheckboxSelection(moduleType, type),
    onRowSelectionModelChange: (newSelection: GridRowSelectionModel) => {
      setSelectionModel(newSelection);
    },
    rowSelectionModel: selectionModel,
    getRowClassName: (params: { row: IDataTableRow }) =>
      displayRowStatusFailedColor(params.row.status ?? ''),
  };

  return (
    <Box data-testid="data-table" className={getTableClassName(type)}>
      <DataGrid key={dataGridKey} rows={currentPageData} columns={columns} {...tableGridProps} />
      <br />

      <TablePaginationRow
        count={pageCount}
        page={page}
        show={currentPageData.length > 0}
        onChange={handlePaginationChange}
      />
    </Box>
  );
};
